import {
  ComputedRef,
  Ref,
  computed,
  onMounted,
  onUnmounted,
  ref,
} from '@vue/composition-api';
import { BoardTab } from '@/models/apis/settouMessageBoard/settouMessageBoardResponse';
import { TabFilterState } from '@/models/settouMessageBoard';
import { createInjection } from '@/lib/createInjection';

interface BoardTabPagerOptions {
  tabState: TabFilterState;
  armMarkNewest: (boardMasterId: number) => void;
}

export interface UseBoardTabPagerResult {
  refTabBar: Ref<HTMLElement | undefined>;
  filteredBoardTabs: ComputedRef<BoardTab[]>;
  beforeFilteredBoardTabs: ComputedRef<BoardTab[]>;
  afterFilteredBoardTabs: ComputedRef<BoardTab[]>;
  isNextDisabled: ComputedRef<boolean>;
  onClickTabListItem: (board_master_id: number) => Promise<void>;
  prevClick: () => void;
  nextClick: () => void;
}

const { provide, inject } = createInjection<UseBoardTabPagerResult>('settouMessageBoard/useBoardTabPager');

export function provideBoardTabPager(options: BoardTabPagerOptions): void {
  provide(buildBoardTabPager(options));
}

export function useBoardTabPager(): UseBoardTabPagerResult {
  return inject();
}

// PC版タブバーの表示範囲制御（画面幅に応じた最大表示数・前後送り・タブリスト選択）を担う composable。
function buildBoardTabPager(options: BoardTabPagerOptions): UseBoardTabPagerResult {
  const { tabState, armMarkNewest } = options;
  const refTabBar = ref<HTMLElement>();

  const filteredBoardTabs = computed<BoardTab[]>(() => {
    const maxIdx = tabState.filteredTabMinIdx + tabState.maxFilteredTabCount;
    return tabState.boardTabs.filter((e, i) => {
      return i >= tabState.filteredTabMinIdx && i < maxIdx;
    });
  });
  const beforeFilteredBoardTabs = computed<BoardTab[]>(() => {
    return tabState.boardTabs.filter((e, i) => {
      return i < tabState.filteredTabMinIdx;
    });
  });
  const afterFilteredBoardTabs = computed<BoardTab[]>(() => {
    const maxIdx = tabState.filteredTabMinIdx + tabState.maxFilteredTabCount;
    return tabState.boardTabs.filter((e, i) => {
      return i >= maxIdx;
    });
  });
  const isNextDisabled = computed<boolean>(() => {
    return tabState.filteredTabMinIdx >= tabState.boardTabs.length - tabState.maxFilteredTabCount;
  });

  const initResizeFunc = (): void => {
    tabState.onResizeFunc = () => {
      // resizeされたときにタブの最大表示数を変更する
      const tabBarW = refTabBar.value?.clientWidth ?? window.innerWidth;
      const btnsW = document.getElementById('tab-pager')?.clientWidth ?? 0;
      tabState.maxFilteredTabCount = parseInt(((tabBarW - btnsW) / 160).toString());
    };
    tabState.onResizeFunc();
    window.addEventListener('resize', tabState.onResizeFunc);
  };
  const onClickTabListItem = async(board_master_id: number): Promise<void> => {
    const boardTabIdx = tabState.boardTabs.findIndex(e => e.id === board_master_id);
    if (boardTabIdx < 0) return;
    tabState.currentTabId = board_master_id;
    armMarkNewest(board_master_id); // タブ切替先の最新投稿を既読化する
    if (boardTabIdx >= tabState.boardTabs.length - tabState.maxFilteredTabCount) {
      tabState.filteredTabMinIdx = tabState.boardTabs.length - tabState.maxFilteredTabCount;
    } else {
      tabState.filteredTabMinIdx = boardTabIdx;
    }
  };
  const prevClick = (): void => {
    if (tabState.filteredTabMinIdx === 0) return;
    tabState.filteredTabMinIdx = tabState.filteredTabMinIdx - 1;
  };
  const nextClick = (): void => {
    if (isNextDisabled.value) return;
    tabState.filteredTabMinIdx = tabState.filteredTabMinIdx + 1;
  };

  onMounted(() => {
    initResizeFunc();
  });
  onUnmounted(() => {
    window.removeEventListener('resize', tabState.onResizeFunc);
  });

  return {
    refTabBar,
    filteredBoardTabs,
    beforeFilteredBoardTabs,
    afterFilteredBoardTabs,
    isNextDisabled,
    onClickTabListItem,
    prevClick,
    nextClick,
  };
}
